/**
 * Cabecera de pantalla: h1 + subtítulo, con acciones opcionales alineadas a la derecha.
 * El maquetado usa tres tamaños de h1 (38px en el panel, 34px normal, 32px en el detalle).
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from './Icon';
import { useEmbedded } from './EmbeddedContext';
import styles from './PageHeader.module.css';

interface PageHeaderProps {
  title: string;
  /** Algo pegado al título, a su derecha (p. ej. el ojo con todos los datos del inmueble). */
  titleAside?: ReactNode;
  subtitle?: string;
  actions?: ReactNode;
  size?: 'default' | 'dashboard' | 'detail';
  /** Ruta del enlace «Volver a…». */
  backTo?: string;
  backLabel?: string;
}

export function PageHeader({
  title,
  titleAside,
  subtitle,
  actions,
  size = 'default',
  backTo,
  backLabel,
}: PageHeaderProps) {
  const embedded = useEmbedded();

  /*
   * Dentro de una pestaña (ver `EmbeddedContext`): ni título ni «volver», que ya los pone la
   * página contenedora. Quedan la frase que explica qué es esto y las acciones.
   */
  if (embedded) {
    if (!subtitle && !actions) return null;
    return (
      <div className={styles.embedded}>
        {subtitle ? <p className={styles.embeddedSubtitle}>{subtitle}</p> : <span />}
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>
    );
  }

  const h1Class = [
    styles.h1,
    size === 'dashboard' ? styles.h1Dashboard : '',
    size === 'detail' ? styles.h1Detail : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {backTo ? (
        <Link to={backTo} className={styles.back}>
          <Icon name="chevron-left" size={16} strokeWidth={2.2} />
          {backLabel ?? 'Volver'}
        </Link>
      ) : null}
      <div className={actions ? styles.header : `${styles.header} ${styles.headerPlain}`}>
        <div>
          {titleAside ? (
            <div className={styles.titleRow}>
              <h1 className={h1Class}>{title}</h1>
              {titleAside}
            </div>
          ) : (
            <h1 className={h1Class}>{title}</h1>
          )}
          {subtitle ? (
            <p className={size === 'detail' ? `${styles.subtitle} ${styles.subtitleSm}` : styles.subtitle}>
              {subtitle}
            </p>
          ) : null}
        </div>
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>
    </>
  );
}
